import React, {useContext, useEffect, useMemo, useState} from "react";
import { FormContext, useForm } from "react-hook-form";
import {Button, Text, Spacer, SubTitle, Dropdown, Toggle} from "@dspworkplace/ui";
import SettingsContext from "../../context";
import {toast} from "../../../../components/Toasts";
import {Link, Status} from "../../../../components/UI";
import apiFactory from "../../../../api/factory";
import Loading, {LoadingWrapper} from "../../../../components/Loading";
import {TabList} from "../../../../components/Tab";
import {useLocalStorage, useAuth} from "../../../../Auth";
import StationOptions from "./options";
import Skeleton from "../../../../components/Skeleton";
import { MenuItemBadge } from "../../../../components/Menus/Main";
import { HelpButton } from "../../../../components/UI";
import RestTimeComplianceWarningUsers from "../RestTimeComplianceWarningUsers";
import { AccessCheck, IsGranted } from "../../../../security";
import { getCompanyId, showToast } from "../../../../Utilities";
import Modal from "../../../../components/Modal";
import styled from "styled-components";

export const RouteAssistTM = () => <span dangerouslySetInnerHTML={{__html: 'RouteAssist&TRADE;'}}/>

const CookieStatus = ({status}) =>
    <Status current={{
        NEEDS_SETUP: 'info',
        IDLE: 'warning',
        NEEDS_REFRESH: 'error',
        ACTIVE: 'success'
    }[status]}>
        {status?.replace('_', ' ') ?? <Skeleton width={40} height={12}/>}
    </Status>

async function updateRestTimeComplianceWarningUsers(api, restTimeComplianceWarningUsers) {
    try {
        await api.post("/api/bot/twilio/employees", {
            companyId: getCompanyId(),
            update: "restTimeComplianceWarningUsers",
            restTimeComplianceWarningUsers,
        });
        return true;
    } catch (e) {
        return false;
    }
}

const Error = styled.div`
    font-size: 12px;
    font-weight: normal;
    font-family: Circe-Rounded;
    display: block;
    color: #516F90;
    margin-top: 4px;
    color: #D94C53;
`;

export default function RestTimeComplianceSettings()
{
    // const { register, handleSubmit, watch, reset } = useForm();
    const {call, cancel} = apiFactory();
    const [state, dispatch] = useContext(SettingsContext);
    const formMethods = useForm();
    const [stations] = useLocalStorage('stations');
    const {handleSubmit, register, watch, reset, errors, getValues} = formMethods;
    const restTimeComplianceWarning = watch("restTimeComplianceWarning");
    const [showHelp, setShowHelp] = useState(false);
    const [restTimeComplianceEmployeementUsersError, setRestTimeComplianceEmployeementUsersError] = useState(false);
    const { api } = useAuth();
    // const { restTimeComplianceWarningUsers } = getValues();

    useEffect(() => {
        reset({
            restTimeComplianceWarning: state.company.restTimeComplianceWarning,
            restTimeComplianceEmployeementStatus: state.company.restTimeComplianceEmployeementStatus,
        });
    }, [state.company]);

    const settings = useMemo(() =>
        state?.restTimeComplianceSetting?.reduce((settings, s) => {
            if (s.station)
                settings.stations[s.station.code] = s;
            else
                settings.all = s;

            return settings;
        }, {all: {}, stations: {}})
    , [state.restTimeComplianceSetting]);


    const fetchRoles = async () =>
        call({
            actions: {
                response: {
                    Role: {
                        findBy: {
                            get: "roles",
                            criteria: {
                                "company": state.company.id
                            },
                            includes: [
                                "id",
                                "roleName",
                                "name"
                            ],
                            orderBy: {
                                'name': 'ASC'
                            }
                        }
                    },
                }
            }
        }).then(response =>
            dispatch({
                type: 'ADD',
                payload: {
                    roles: response.data.data.roles
                        .map(r => ({name: r.roleName, value: r.name}))
                        .filter(r => r.value !== 'ROLE_ALLOWED_TO_SWITCH')
                        .sort((a, b) => a.name.localeCompare(b.name))
                }
            })
        )

    const handleSave = async ( data, evt ) => {
        let form = evt.target;

        // form.querySelector('button').disabled = true;

        if (data?.restTimeComplianceWarningUsers?.length == 0) {
            setRestTimeComplianceEmployeementUsersError(true);
            return false; // no users selected, nothing to do
        }
        setRestTimeComplianceEmployeementUsersError(false);
        let updateRecord = {
            restTimeComplianceWarning: data.restTimeComplianceWarning,
            restTimeComplianceEmployeementStatus: data.restTimeComplianceEmployeementStatus,
        };
        await api.post("/api/lazy/manage/data", {
            actions: {
                update: {
                    Company: {
                        update_company: {
                            findBy: {
                                id: getCompanyId(),
                            },
                            updateRecord: updateRecord,
                        },
                    },
                },
            },
        });
        let updatedRestTimeComplianceWarning = state.company;
        updatedRestTimeComplianceWarning['restTimeComplianceWarning'] = data.restTimeComplianceWarning;
        updatedRestTimeComplianceWarning['restTimeComplianceWarningUsers'] = data.restTimeComplianceWarningUsers;
        dispatch({type: 'UPDATE_COMPANY', payload: {company: {...updatedRestTimeComplianceWarning }}})
        if (data.restTimeComplianceWarning == true) {
            await updateRestTimeComplianceWarningUsers(api, data.restTimeComplianceWarningUsers);
            updateRecord = {
                ...updateRecord,
                ...{ restTimeComplianceWarningUsers: data.restTimeComplianceWarningUsers },
            };
        } else {
            return true;
        }

        // // form.querySelector("button").disabled = false;
        // await dispatch({ type: "UPDATE_COMPANY", payload: { company: { ...state.company, ...updateRecord } } });

        const record = Object.entries(data).reduce((all, [prop, value]) => {
            all[prop] = (typeof value === 'object') ? JSON.stringify(value) : value
            return all;
        }, {});

        delete record.restTimeComplianceWarning;
        delete record.restTimeComplianceEmployeementStatus;
        delete record.restTimeComplianceWarningUsers;

        record.restTimeThreshold = record.restTimeThresholdHour + ':' + record.restTimeThresholdMinute;
        delete record.restTimeThresholdHour;
        delete record.restTimeThresholdMinute;
        record.sendReportAt = record.sendReportAtHour + ':' + record.sendReportAtMinute;
        delete record.sendReportAtHour;
        delete record.sendReportAtMinute;

        record.company = state.company.id;
        const id = record.id;
        delete record.id;

        let params = {
            actions: {
                update: {
                    RestTimeComplianceSetting: {}
                },
                create: {
                    RestTimeComplianceSetting: {}
                }
            }
        };
        
        // let settingIds = [];
        // if ( Object.keys(settings.all).length ) {
        //     settingIds.push(settings.all.id);
        // }
        // if ( Object.keys(settings.stations).length ) {
        //     Object.values(settings.stations).forEach(station => {
        //         settingIds.push(station.id);
        //     })
        // }
        // settingIds.forEach((settingId, index) => {
        //     params.actions.update.RestTimeComplianceSetting["a"+index] = {
        //         findBy: {
        //           id:settingId
        //         },
        //         updateRecord: {
        //             cortexDspId: record.cortexDspId,
        //             ementorUsername: record.ementorUsername,
        //             ementorPassword: record.ementorPassword,
        //             netradyneUsername: record.netradyneUsername,
        //             netradynePassword: record.netradynePassword,
        //         }
        //     };
        // });

        if ( Object.keys(settings.all).length ) {
        } else {
            params.actions.create.RestTimeComplianceSetting.c1 = record;
        }

        if ( !id ) {
            params.actions.create.RestTimeComplianceSetting = {
                create: record
            }
        } else {
            params.actions.update.RestTimeComplianceSetting.u3 = {
                findBy: {
                    id
                },
                updateRecord: record
            };
        }

        console.log(params);
        
        call(params).then(
            response => {
                toast({
                    title: <>Rest Time Compliance Setting information updated</>,
                    type: 'info',
                    timeout: 8000,
                    useIcon: true,
                    useClose: true
                });
            }
        ).then(
            () => {
                // form.querySelector('button').disabled = false
            }
        );
    };

    useEffect(() => {
        if (state.company.id)
            fetchRoles()

        return cancel;
    }, [state.company.id]);

    const isLoading = !state.roles?.length;


    return <FormContext {...formMethods}>
        <section>
            <form onSubmit={handleSubmit(handleSave)}>
                <SubTitle>
                    Rest Time Compliance Warning
                    <HelpButton onClick={() => setShowHelp(true)}>How Does It Work?</HelpButton>
                </SubTitle>
                <Spacer bottom={2} />
                <Toggle
                    ref={register}
                    name="restTimeComplianceWarning"
                    singleText="Send notification when a driver is scheduled to fail rest time compliance"
                    on={restTimeComplianceWarning}
                />
                {restTimeComplianceWarning ? (
                    <Spacer top={4}>
                        <label
                            style={{
                                fontSize: "16px",
                                fontWeight: "normal",
                                fontFamily: "Circe-Rounded",
                                display: "block",
                                paddingBottom: "2px",
                                color: "#707070",
                            }}
                        >
                            {/* Employeement status to watch */}
                        </label>
                        <Dropdown
                            name="restTimeComplianceEmployeementStatus"
                            label="Employeement status to watch"
                            ref={register}
                            placeholder="Employeement status to watch"
                            options={[
                                { name: "Any", value: "any" },
                                { name: "Exempt", value: "exempt" },
                                { name: "Non-Exempt", value: "non-exempt" }
                            ]}
                            style={{ marginRight: "5px", fontSize: "12px" }}
                            size="medium"
                            valid={"true"}
                        />
                        <Spacer bottom={5} />
                        <RestTimeComplianceWarningUsers ref={register} />
                        <Error>{restTimeComplianceEmployeementUsersError == true && "x Field Required"}</Error>
                        <Spacer bottom={5} />
                        <div style={{position: 'relative'}}>
                            {isLoading ? <LoadingWrapper><Loading/></LoadingWrapper> : null}
                            <TabList
                                defaultTab={'all'}
                                tabs={{
                                    all: {
                                        title: 'All Stations',
                                        content: <StationOptions settings={settings?.all}/>
                                    },
                                    ...stations.sort((a, b) => {
                                        return a.code.localeCompare(b.code, undefined, {numeric: true})
                                    }).reduce((all, s) => {
                                        all[s.code] = {
                                            title: <>{s.code} {settings?.stations?.[s.code]?.enabled ? <MenuItemBadge>custom</MenuItemBadge> : null}</>,
                                            content: <StationOptions settings={settings?.stations?.[s.code]} station={s}/>
                                        }
                                        return all;
                                    }, {})
                                }}
                                onChange={() => {
                                    formMethods.unregister(
                                        Object.keys(formMethods.getValues())
                                            .filter(k => ![
                                                'restTimeThresholdHour',
                                                'restTimeThresholdMinute',
                                                'sendReportAtHour',
                                                'sendReportAtMinute'
                                            ].includes(k))
                                    )
                                }}
                            />
                        </div>
                    </Spacer>
                ):<>
                    <Spacer bottom={5} />
                    <Button type='primary'>Save</Button>
                </>}
                {/* <IsGranted feature={AccessCheck.features.SETTINGS} ops={[AccessCheck.OPS.U]}>
                    <Spacer bottom={5} />
                    <Button type="primary">Save</Button>
                </IsGranted> */}
                {!showHelp ? null : (
                    <Modal
                        visible={true}
                        setVisible={() => setShowHelp(false)}
                        title={"Rest Time Compliance Warning"}
                        drawer={"right"}
                    >
                        <Text>
                            Rest period is measured from the punch out of an employee's last scheduled shift and the
                            start time of their first scheduled shift the following day. If you set the rest time requirement
                            to be 10.0 hours, we will look at the schedule for the next day and send a workbook alert
                            listing those currently at risk of violating the rest period rule. Note: Missed punch-out times
                            may also generate an alert if the driver works an early morning shift!
                        </Text>
                        <Spacer bottom={5} />
                    </Modal>
                )}
                <Spacer bottom={5}/>

                

                <Spacer bottom={10}/>
            </form>
        </section>
    </FormContext>
}